import React, { useState } from 'react';
import { CheckoutConfig, PlanDetail } from '../../../types';

interface ShareModalProps {
  config: CheckoutConfig;
  selectedPlan: PlanDetail;
  onClose: () => void;
}

export const CheckoutStudioShareModal: React.FC<ShareModalProps> = ({ config, selectedPlan, onClose }) => {
  const [activeTab, setActiveTab] = useState<'link' | 'iframe' | 'react' | 'script'>('link');
  const [cycle, setCycle] = useState<'monthly' | 'quarterly' | 'annually'>('monthly');
  const [coupon, setCoupon] = useState('');
  const [copied, setCopied] = useState(false);

  const baseUrl = window.location.origin;
  const planParam = `tier=${encodeURIComponent(selectedPlan.tier.toLowerCase())}`;
  const cycleParam = cycle !== 'monthly' ? `&cycle=${cycle}` : '';
  const couponParam = coupon.trim() ? `&coupon=${encodeURIComponent(coupon.trim().toUpperCase())}` : '';
  const shareUrl = `${baseUrl}/checkout?${planParam}${cycleParam}${couponParam}`;

  const iframeCode = `<iframe
  src="${shareUrl}"
  width="100%"
  height="720px"
  style="border: none; border-radius: ${config.buttonRadius || '12px'}; max-width: 900px; margin: 0 auto; display: block;"
  allow="payment"
></iframe>`;

  const reactCode = `import { CheckoutModal } from '@subscale/react';

export default function PricingSection() {
  return (
    <CheckoutModal
      planId="${selectedPlan.id}"
      tier="${selectedPlan.tier}"
      template="${config.template}"
      primaryColor="${config.primaryColor}"
      onSuccess={(order) => console.log('Paid:', order)}
    />
  );
}`;

  const scriptCode = `<!-- Subscale Universal Checkout Integration -->
<button data-subscale-checkout="${selectedPlan.id}" data-theme="${config.template}">
  Subscribe to ${selectedPlan.tier}
</button>
<script src="https://apis.inventechsolution.com/v1/checkout.js" async></script>`;

  const getActiveCode = () => {
    if (activeTab === 'link') return shareUrl;
    if (activeTab === 'iframe') return iframeCode;
    if (activeTab === 'react') return reactCode;
    return scriptCode;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getActiveCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col text-left">
        {/* Header */}
        <div className="p-4 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" x2="12" y1="2" y2="15" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Share & Embed Checkout</h3>
              <p className="text-[11px] text-slate-500">Deploy your custom checkout via direct URL, iframe, or React</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" x2="6" y1="6" y2="18" />
              <line x1="6" x2="18" y1="6" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-4 bg-slate-50/50 dark:bg-slate-900/50 gap-2">
          {[
            { id: 'link', label: 'Hosted URL' },
            { id: 'iframe', label: 'Embed iFrame' },
            { id: 'react', label: 'React Snippet' },
            { id: 'script', label: 'HTML Script' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id as any)}
              className={`py-2.5 px-3 text-xs font-bold border-b-2 transition-all ${
                activeTab === t.id
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Modal Body */}
        <div className="p-4 space-y-4">
          {/* Query Parameters Configurator */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase">Preselected Cycle</label>
              <select
                value={cycle}
                onChange={(e) => setCycle(e.target.value as any)}
                className="w-full text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none mt-1"
              >
                <option value="monthly">Monthly Billing</option>
                <option value="quarterly">Quarterly Billing</option>
                <option value="annually">Annual Billing</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase">Auto-Applied Coupon</label>
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="e.g. VIP20"
                className="w-full text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none mt-1 uppercase"
              />
            </div>
          </div>

          {/* Code Container */}
          <div className="relative">
            <pre className="p-3 bg-slate-950 text-slate-200 text-xs font-mono rounded-xl border border-slate-800 overflow-x-auto max-h-40 custom-scrollbar whitespace-pre-wrap break-all">
              {getActiveCode()}
            </pre>
            <button
              type="button"
              onClick={handleCopy}
              className="absolute top-2.5 right-2.5 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-md shadow flex items-center gap-1.5 transition-all"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
